import { useState } from "react";
import { useVault } from "../vault/store";
import { distributeKeys, useDistributionPlan } from "../vault/access-keys";
import { summarize, STATUS_REASON, type DistRow, type DistStatus } from "../vault/access-plan";

// Key distribution: the second half of granting access.
//
// A grant authorizes; it hands out no data. Reading a vault needs its org_key wrapped onto the
// user's public key, and only a browser holding that key can produce the wrap (§7) — the server
// never sees it, and it cannot be wrapped to a group at all. So this runs HERE, in the admin's
// browser, over the gaps the backend reports. See vault/access-keys.ts for the mechanics.

function StatusCell({ status }: { status: DistStatus }) {
  const cls: Record<DistStatus, string> = {
    ready: "bg-ok/20 text-ok",
    "not-enrolled": "bg-warn/20 text-warn",
    "no-admin-key": "bg-warn/20 text-warn",
  };
  const label: Record<DistStatus, string> = {
    ready: "ready",
    "not-enrolled": "not enrolled",
    "no-admin-key": "not your vault",
  };
  return (
    <span title={STATUS_REASON[status]} className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls[status]}`}>
      {label[status]}
    </span>
  );
}

/**
 * The standing reconciliation view: who is authorized without a key, and one click to fix it.
 * `userIds` narrows it to the people a just-created grant affected.
 */
export function KeyDistribution({ userIds, compact }: { userIds?: string[]; compact?: boolean }) {
  const vault = useVault();
  const { rows, orgs, err, loading, reload } = useDistributionPlan(userIds);
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // Rows are opt-OUT: everything actionable is picked, because the normal case is "all of it".
  // Excluding a row still has to be possible — handing a vault key over is handing over its
  // passwords, and e.g. a break-glass account may be authorized on everything by design
  // without anyone wanting it to hold every key.
  const [excluded, setExcluded] = useState<Record<string, boolean>>({});

  const locked = vault.phase !== "unlocked";
  const rowKey = (r: DistRow) => r.orgId + ":" + r.userId;
  const picked = (rows ?? []).filter((r) => r.status === "ready" && !excluded[rowKey(r)]);

  async function run() {
    if (picked.length === 0) return;
    setBusy(true);
    setLog([`Distributing keys for ${picked.length} assignment(s)…`]);
    const res = await distributeKeys(picked, orgs, vault.orgKeyFor, (l) => setLog((p) => [...p, l]));
    setLog((p) => [...p, `Done: ${res.ok} distributed, ${res.failed} failed.`]);
    setBusy(false);
    reload();
  }

  if (err) return <p className="rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">{err}</p>;
  if (loading || !rows) return <p className="text-sm text-muted">Loading…</p>;

  const s = summarize(rows);
  if (s.total === 0) {
    return (
      <p className="text-sm text-muted">
        Everyone authorized holds the key for the vaults they may use. Nothing to distribute.
      </p>
    );
  }

  return (
    <div className={compact ? "" : "max-w-3xl"}>
      {!compact && (
        <p className="mb-3 text-sm text-muted">
          These people are <b>authorized</b> but hold no vault key, so the vault does not even appear
          for them. The key is wrapped to each person here in your browser — the server never sees it,
          and it cannot be wrapped to a group.
        </p>
      )}
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted">
          <tr>
            <th className="py-1 w-6"></th>
            <th className="py-1">Vault</th><th>User</th><th>Authorized via</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const k = rowKey(r);
            return (
              <tr key={k} className="border-t border-border">
                <td className="py-1.5">
                  {r.status === "ready" && (
                    <input
                      type="checkbox"
                      checked={!excluded[k]}
                      onChange={(e) => setExcluded((p) => ({ ...p, [k]: !e.target.checked }))}
                      aria-label={`Give ${r.login} the key to ${r.orgName}`}
                    />
                  )}
                </td>
                <td className="py-1.5">{r.orgName}</td>
                <td>{r.login}</td>
                <td className="text-xs text-muted">{r.via}</td>
                <td className="py-1.5"><StatusCell status={r.status} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {s.ready > 0 && (
        <button
          onClick={() => setExcluded(picked.length > 0
            ? Object.fromEntries(rows.filter((r) => r.status === "ready").map((r) => [rowKey(r), true]))
            : {})}
          className="mt-2 text-xs text-accent hover:underline"
        >
          {picked.length > 0 ? "clear selection" : "select all"}
        </button>
      )}

      {s.blocked > 0 && (
        <ul className="mt-3 flex flex-col gap-1 text-xs text-muted">
          {s.byStatus["not-enrolled"] > 0 && <li>⚠ {s.byStatus["not-enrolled"]}× {STATUS_REASON["not-enrolled"]}</li>}
          {s.byStatus["no-admin-key"] > 0 && <li>⚠ {s.byStatus["no-admin-key"]}× {STATUS_REASON["no-admin-key"]}</li>}
        </ul>
      )}

      {locked && (
        <p className="mt-3 rounded border border-warn/40 bg-warn/10 p-2 text-xs text-warn">
          Unlock the vault first — handing a key on requires unwrapping it with your own key.
        </p>
      )}

      {log.length > 0 && (
        <div className="mt-3 max-h-40 overflow-auto rounded bg-panel2 p-2 font-mono text-xs">
          {log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={run}
          disabled={busy || locked || picked.length === 0}
          className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2 disabled:opacity-50"
        >
          {busy ? "Distributing…" : `Distribute keys (${picked.length})`}
        </button>
        <span className="text-xs text-muted">
          {s.ready} of {s.total} can be done now; {s.users} user(s) affected.
        </span>
      </div>
    </div>
  );
}
